import {
  formatCurrency,
  formatCurrencyRange,
  normalizePricingForDisplay,
} from './formatPrice';
import {
  resolveSubserviceTagline,
  resolveSubserviceTitle,
} from './subserviceDisplay';

const AVAILABILITY_LABELS = {
  asap: 'Available ASAP',
  today: 'Available today',
  'few-days': 'Available in a few days',
};

/** Backend slot keys — fastest response first for tie-breakers */
const AVAILABILITY_SLOT_ORDER = ['asap', 'today', 'few-days'];

const QUOTING_METHOD_LABELS = {
  phone: 'Phone',
  'in-person': 'In-person',
  email: 'Email',
  online: 'Online',
};

const QUOTE_REQUIRED_LABEL = 'Quote required';

function titleCaseSlug(slug) {
  if (!slug || typeof slug !== 'string') return '';
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function toNumber(value) {
  if (value == null || value === '') return null;
  const num = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''));
  return Number.isFinite(num) ? num : null;
}

/**
 * Pick one slot key to display: match the timing answer when possible, else fastest offered.
 * @param {string[]} slots
 * @param {string | null | undefined} preferredSlotKey
 * @returns {string | null}
 */
export function pickAvailabilitySlotKey(slots, preferredSlotKey) {
  const list = Array.isArray(slots)
    ? [...new Set(slots)].filter((s) => typeof s === 'string' && s.length > 0)
    : [];
  if (list.length === 0) return null;

  if (
    preferredSlotKey &&
    AVAILABILITY_SLOT_ORDER.includes(preferredSlotKey) &&
    list.includes(preferredSlotKey)
  ) {
    return preferredSlotKey;
  }

  for (const key of AVAILABILITY_SLOT_ORDER) {
    if (list.includes(key)) return key;
  }

  return list[0];
}

/** @param {string | null | undefined} slotKey */
export function formatAvailabilityLabelForSlot(slotKey) {
  if (!slotKey || typeof slotKey !== 'string') return null;
  return AVAILABILITY_LABELS[slotKey] || titleCaseSlug(slotKey);
}

/** Labels for quote-method chips on provider cards */
export function getQuotingMethodLabels(quoting) {
  if (!quoting || typeof quoting !== 'object') return [];
  return Array.isArray(quoting.QuotingMethod)
    ? quoting.QuotingMethod.map(
        (m) => QUOTING_METHOD_LABELS[m] || titleCaseSlug(String(m)),
      ).filter(Boolean)
    : [];
}

/**
 * Search card price for one sub-service: "$150", "$85–$220", "From $85", "Up to $220", or "Quote required".
 * `sortValue` is the lowest listed amount (null when no price is shown).
 * @param {object | null | undefined} pricing API `Pricing`
 * @returns {{ label: string, sortValue: number | null }}
 */
export function formatSearchPrice(pricing) {
  const normalized = normalizePricingForDisplay(pricing);
  if (normalized?.PriceType === 'Fixed') {
    const label = formatCurrency(normalized.Price);
    if (label) return { label, sortValue: toNumber(normalized.Price) };
  }
  if (normalized?.PriceType === 'Range') {
    const label = formatCurrencyRange(normalized.Min, normalized.Max, { separator: '–' });
    if (label) {
      return {
        label,
        sortValue: toNumber(normalized.Min) ?? toNumber(normalized.Max),
      };
    }
  }
  return { label: QUOTE_REQUIRED_LABEL, sortValue: null };
}

/**
 * "Show all pros" card price: the lowest listed amount across the provider's sub-services.
 * @param {object[]} subServices
 * @returns {{ label: string, sortValue: number | null }}
 */
export function formatLowestSearchPrice(subServices) {
  const lowest = (Array.isArray(subServices) ? subServices : [])
    .map((sub) => formatSearchPrice(sub?.Pricing).sortValue)
    .filter((value) => value != null)
    .reduce((min, value) => (min == null || value < min ? value : min), null);
  if (lowest == null) return { label: QUOTE_REQUIRED_LABEL, sortValue: null };
  return { label: `Prices from ${formatCurrency(lowest)}`, sortValue: lowest };
}

/**
 * Maps a GET /service-api/?includeBusinesses=true row to the results card model.
 * `NKYCPS` is intentionally not carried over: it ranks results (API order) but is never shown.
 *
 * @param {object} row ServiceWithBusinessDetails-shaped API row
 * @param {{ task?: string | null, timing?: string | null, order?: number }} search
 *   `task` — sub-service to price and tag; null = all-pros card ("Prices from $X").
 */
export function mapServiceRowToSearchCard(row, { task = null, timing = null, order = 0 } = {}) {
  const subs = Array.isArray(row.SubServices) ? row.SubServices : [];
  const matched = task ? subs.find((s) => s.SubServiceType === task) : null;

  const rating = toNumber(row.AverageRating);
  const reviewCount = toNumber(row.ReviewCount) ?? 0;
  const googleRating = toNumber(row.GoogleReview?.Rating);
  const googleReviewCount = toNumber(row.GoogleReview?.ReviewCount) ?? 0;
  const hasGoogleReview = googleRating != null && googleReviewCount > 0;

  const availabilitySlots = Array.isArray(row.Availability) ? row.Availability : [];
  const availabilitySlot = pickAvailabilitySlotKey(
    availabilitySlots,
    AVAILABILITY_SLOT_ORDER.includes(timing) ? timing : null,
  );

  const quoting = row.Quoting || {};
  const price = matched ? formatSearchPrice(matched.Pricing) : formatLowestSearchPrice(subs);
  const city = row.BusinessAddress?.City?.trim() || '';

  return {
    BusinessId: row.BusinessId,
    BusinessSlug: row.BusinessSlug || '',
    BusinessName: row.BusinessName || 'Business',
    BusinessPhotoUrl: row.BusinessPhotoUrl || null,
    BusinessPhone: row.BusinessPhoneNumber || '',

    city: city || 'Northern Kentucky',
    rating: reviewCount > 0 ? rating : null,
    reviewCount,
    googleRating: hasGoogleReview ? googleRating : null,
    googleReviewCount: hasGoogleReview ? googleReviewCount : 0,

    availabilityLabel: formatAvailabilityLabelForSlot(availabilitySlot),
    availabilityUrgent: availabilitySlot === 'asap' || availabilitySlot === 'today',

    taskLabel: matched ? resolveSubserviceTitle(row.ServiceType, matched) : null,
    tagline: matched ? resolveSubserviceTagline(matched) : null,
    priceLabel: price.label,
    priceSortValue: price.sortValue,

    hasFreeQuotes: Boolean(quoting.FreeQuotes),
    quotingMethodLabels: getQuotingMethodLabels(quoting),
    hasFinancing: row.FinancingAvailable === true,

    bestMatchOrder: order,
  };
}

/** Results filter chips. */
export const SEARCH_RESULT_FILTERS = [
  { id: 'freeQuotes', label: 'Free quotes', test: (card) => card.hasFreeQuotes },
  { id: 'priceShown', label: 'Price shown', test: (card) => card.priceSortValue != null },
  { id: 'financing', label: 'Financing', test: (card) => card.hasFinancing },
];

/** Results sort options; best match is the API order (ranked by NKYCPS server-side). */
export const SEARCH_RESULT_SORTS = [
  { id: 'bestMatch', label: 'Best match' },
  { id: 'rating', label: 'Highest rated' },
  { id: 'price', label: 'Lowest price' },
];

function compareProvidersByName(a, b) {
  return String(a.BusinessName || '').localeCompare(String(b.BusinessName || ''), undefined, {
    sensitivity: 'base',
  });
}

/** Best match restores the API response order; ties break on business name. */
export function compareProvidersByBestMatch(a, b) {
  const oa = a.bestMatchOrder ?? Number.MAX_SAFE_INTEGER;
  const ob = b.bestMatchOrder ?? Number.MAX_SAFE_INTEGER;
  if (oa !== ob) return oa - ob;
  return compareProvidersByName(a, b);
}

/** Highest rated: NKY Connect average, else Google rating; unrated last. */
export function compareProvidersByRating(a, b) {
  const ra = a.rating ?? a.googleRating;
  const rb = b.rating ?? b.googleRating;
  if (ra == null && rb == null) return compareProvidersByBestMatch(a, b);
  if (ra == null) return 1;
  if (rb == null) return -1;
  if (rb !== ra) return rb - ra;
  return compareProvidersByBestMatch(a, b);
}

/** Lowest price first; "Quote required" last. */
export function compareProvidersByPrice(a, b) {
  const pa = a.priceSortValue;
  const pb = b.priceSortValue;
  if (pa == null && pb == null) return compareProvidersByBestMatch(a, b);
  if (pa == null) return 1;
  if (pb == null) return -1;
  if (pa !== pb) return pa - pb;
  return compareProvidersByBestMatch(a, b);
}

const SORT_COMPARATORS = {
  bestMatch: compareProvidersByBestMatch,
  rating: compareProvidersByRating,
  price: compareProvidersByPrice,
};

/**
 * Filters then sorts results cards.
 * @param {object[]} cards
 * @param {string[]} activeFilterIds ids from {@link SEARCH_RESULT_FILTERS}
 * @param {'bestMatch' | 'rating' | 'price'} sortBy
 */
export function applySearchFiltersAndSort(cards, activeFilterIds, sortBy) {
  const active = SEARCH_RESULT_FILTERS.filter((f) => activeFilterIds.includes(f.id));
  const list = cards.filter((card) => active.every((f) => f.test(card)));
  return list.sort(SORT_COMPARATORS[sortBy] || compareProvidersByBestMatch);
}
